// Route -> Segment page(category, name) mapping. Source of truth:
// AnalyticsInstrumentation/schemas/apps/mtag/values/page.json. The SDK auto-captures
// path/url/title/referrer/search; category + name are the two page() arguments.
//
// `pageType` is the machine-readable counterpart to `name`, agreed with analytics on the
// MB3ENG-7214 thread. No event schema declares it, so it does not ride the payload — it is
// stamped onto Segment's `context.page` by the source middleware in `useAnalytics`, which
// is where MB3 carries it and where the warehouse reads it from.
export type PageType =
  | 'pdp'
  | 'specs'
  | 'pairs_with'
  | 'similar_products'
  | 'cart'
  | 'brand_home'
  | 'brand_settings'

export type PageConfig = {
  category: string
  name: string
  pageType: PageType
}

type PageConfigEntry = PageConfig & { pattern: RegExp }

// Strip an optional `/xx-XX` locale prefix (next-intl `as-needed`) so patterns match
// regardless of locale.
function stripLocale(pathname: string): string {
  return pathname.replace(/^\/[a-z]{2}-[A-Z]{2}(?=\/|$)/, '')
}

// Order matters — more specific patterns first. Names come from
// AnalyticsInstrumentation/schemas/apps/mtag/values/page.json; keep them spelled exactly
// as the catalog does, since `name` is the join key on the warehouse side.
const PAGE_CONFIGS: PageConfigEntry[] = [
  {
    pattern: /^\/tag\/[^/]+\/pairs-with$/,
    category: 'Discovery',
    name: 'Pairs With',
    pageType: 'pairs_with',
  },
  {
    pattern: /^\/tag\/[^/]+(\/product\/[^/]+)?\/similar$/,
    category: 'Discovery',
    name: 'Similar Products',
    pageType: 'similar_products',
  },
  {
    pattern: /^\/tag\/[^/]+\/cart$/,
    category: 'Shopping',
    name: 'Cart',
    pageType: 'cart',
  },
  // The catalog models specs and the drilled-into PDP as surfaces of Product Details
  // rather than pages of their own, so they share a `name`. `pageType` is the finer
  // dimension and does distinguish them, which is what the QA round asked for.
  {
    pattern: /^\/tag\/[^/]+(\/product\/[^/]+)?\/specs$/,
    category: 'Discovery',
    name: 'Product Details',
    pageType: 'specs',
  },
  {
    pattern: /^\/tag\/[^/]+\/product\/[^/]+$/,
    category: 'Discovery',
    name: 'Product Details',
    pageType: 'pdp',
  },
  {
    pattern: /^\/tag\/[^/]+$/,
    category: 'Discovery',
    name: 'Product Details',
    pageType: 'pdp',
  },
  {
    pattern: /^\/admin\/settings/,
    category: 'Brand',
    name: 'Brand Settings',
    pageType: 'brand_settings',
  },
  {
    pattern: /^\/admin\/?$/,
    category: 'Brand',
    name: 'Brand Home',
    pageType: 'brand_home',
  },
]

// Returns the canonical page call for a route, or null for a route that matches none of the
// patterns above — an uncategorized surface the spec does not name, which falls back to an
// uncategorized page call.
export function getPageConfig(pathname: string): PageConfig | null {
  const path = stripLocale(pathname)

  for (const entry of PAGE_CONFIGS) {
    if (entry.pattern.test(path)) {
      return {
        category: entry.category,
        name: entry.name,
        pageType: entry.pageType,
      }
    }
  }

  return null
}

// The page type for the route the browser is on right now. Used by the source middleware
// and by `impression_event`, both of which read the live location rather than a React prop.
export function getCurrentPageType(): PageType | null {
  if (typeof window === 'undefined') {
    return null
  }

  return getPageConfig(window.location.pathname)?.pageType ?? null
}
